import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../api/client';
import { billNumberOf, posApi, type PosSale } from '../api/pos';
import { money } from '../format';

type MoneyMoved = 'none' | 'collected' | 'handedBack';

/**
 * Asking an admin to edit a bill, or to exchange books on it.
 *
 * The till does neither itself. An exchange in particular has to be made by
 * an admin, because its money belongs to the day it happens rather than the
 * bill's day — the bill's day has already been cashed up. What the counter
 * does is say what the customer wants and what money, if any, it has already
 * taken or handed over, while the customer is still standing there.
 */
export default function RequestChange({
  sale,
  kind,
  onCancel,
  onSent,
}: {
  sale: PosSale;
  kind: 'EDIT' | 'EXCHANGE';
  onCancel: () => void;
  onSent: (updated: PosSale) => void;
}) {
  const [reason, setReason] = useState('');
  const [moved, setMoved] = useState<MoneyMoved>('none');
  const [amountText, setAmountText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const exchange = kind === 'EXCHANGE';

  useEffect(() => {
    reasonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, sending]);

  const amountValue = Math.round(Number(amountText || 0) * 100) / 100;

  async function send() {
    if (sending) return;
    if (!reason.trim()) {
      setError(
        exchange
          ? 'Say which books are coming back and which the customer is taking.'
          : 'Say what needs changing on this bill.',
      );
      reasonRef.current?.focus();
      return;
    }
    if (moved !== 'none' && !(amountValue > 0)) {
      setError('Enter the amount that changed hands.');
      return;
    }
    const signed = moved === 'collected' ? amountValue : moved === 'handedBack' ? -amountValue : 0;
    setSending(true);
    setError(null);
    try {
      const updated = await posApi.requestChange(sale.id, {
        kind,
        reason: reason.trim(),
        ...(signed !== 0 ? { amount: signed } : {}),
      });
      onSent(updated);
    } catch (err) {
      setError(errorMessage(err, 'The request could not be sent.'));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        e.stopPropagation();
        if (!sending) onCancel();
      }}
    >
      <div
        className="modal modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-change-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="request-change-title" className="confirm-title">
          {exchange ? 'Ask an admin to exchange books on' : 'Ask an admin to edit'}{' '}
          {billNumberOf(sale)}
        </h2>
        <p className="muted small">
          {exchange
            ? 'An admin makes the exchange. The bill stays as it was sold, and the difference counts in today’s takings.'
            : 'An admin makes the change from the admin panel.'}{' '}
          They will be emailed straight away.
        </p>

        <label className="field-label" htmlFor="change-reason">
          {exchange ? 'What is being exchanged?' : 'What needs changing?'}
        </label>
        <textarea
          id="change-reason"
          ref={reasonRef}
          className="input edit-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder={
            exchange
              ? 'Returned "Wings of Fire" (₹320), took "The Alchemist" (₹340)'
              : 'Billed 2 copies, customer took 1'
          }
        />

        <span className="field-label">Did any money change hands?</span>
        <div className="change-money-row" role="radiogroup" aria-label="Money">
          <label className="change-money-option">
            <input
              type="radio"
              name="money-moved"
              checked={moved === 'none'}
              onChange={() => setMoved('none')}
            />
            <span>No</span>
          </label>
          <label className="change-money-option">
            <input
              type="radio"
              name="money-moved"
              checked={moved === 'collected'}
              onChange={() => setMoved('collected')}
            />
            <span>Collected from customer</span>
          </label>
          <label className="change-money-option">
            <input
              type="radio"
              name="money-moved"
              checked={moved === 'handedBack'}
              onChange={() => setMoved('handedBack')}
            />
            <span>Handed back to customer</span>
          </label>
        </div>
        {moved !== 'none' && (
          <>
            <label className="field-label" htmlFor="change-amount">
              Amount (₹)
            </label>
            <input
              id="change-amount"
              className="input"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
              placeholder="20"
            />
            {amountValue > 0 && (
              <p className="muted small">
                {moved === 'collected'
                  ? `${money(amountValue)} goes in the drawer now.`
                  : `${money(amountValue)} comes out of the drawer now.`}
              </p>
            )}
          </>
        )}

        {error && (
          <p className="error error-block" role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={sending}>
            Go back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void send()}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send to admin'}
          </button>
        </div>
      </div>
    </div>
  );
}
